import { FC, ReactNode, useCallback, useState } from 'react';

import { useMediaQuery } from '@/shared/libs/hooks/useMediaQuery';
import { cn } from '@/shared/libs/utils/cn';

import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from './tooltip';

interface TooltipSimpleProps {
  content: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  contentClassName?: string;
}

/** (hover: none) = touch-only device, no hover support */
const TOUCH_MEDIA_QUERY = '(hover: none)';

/** Минимальная зона нажатия ~44px (рекомендация Apple HIG). -m-3 компенсирует padding, чтобы layout не сдвигался. */
const TOUCH_TARGET_CLASSES = 'p-3 -m-3';

export const TooltipSimple: FC<TooltipSimpleProps> = ({
  children,
  className,
  content,
  contentClassName,
  disabled = false,
  side = 'top',
}) => {
  const isTouchDevice = useMediaQuery(TOUCH_MEDIA_QUERY);
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
  }, []);

  const handleTriggerClick = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handlePointerDownOutside = useCallback(() => {
    setOpen(false);
  }, []);

  if (disabled || !content) {
    return <>{children}</>;
  }

  if (isTouchDevice) {
    return (
      <Tooltip delayDuration={0} onOpenChange={handleOpenChange} open={open}>
        <TooltipTrigger asChild>
          <div
            className={cn(
              className,
              'inline-flex items-center justify-center',
              TOUCH_TARGET_CLASSES,
            )}
            onClick={handleTriggerClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTriggerClick();
              }
            }}
            role="button"
            tabIndex={0}>
            {children}
          </div>
        </TooltipTrigger>
        <TooltipContent
          className={contentClassName}
          onPointerDownOutside={handlePointerDownOutside}
          side={side}>
          {typeof content === 'string' ? (
            <div dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            content
          )}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={className}>{children}</div>
      </TooltipTrigger>
      <TooltipContent side={side} className={contentClassName}>
        {typeof content === 'string' ? (
          <div dangerouslySetInnerHTML={{ __html: content }} />
        ) : (
          content
        )}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
};
